import { EnhancedOrderBox, BoxStatusCode } from '../types/logistics';

// ===========================================
// Delivery Readiness State Types
// ===========================================

export type DeliveryReadinessState =
  | 'not_started'          // All boxes still WCAC/WCFC
  | 'in_transit'           // Some boxes ITKK
  | 'warehouse_processing' // Some boxes WKKU/WKKS/WKKDO/WKKP
  | 'awaiting_load'        // Some boxes WKKHO (ready for courier)
  | 'in_delivery'          // Some boxes ITTC (loaded, delivering)
  | 'completed';           // All boxes WC or terminal

export interface DeliveryReadinessCounts {
  awaitingCollection: number;
  inTransit: number;
  warehouseProcessing: number;
  readyToLoad: number;
  outForDelivery: number;
  delivered: number;
  failed: number;
  total: number;
}

export interface DeliveryReadiness {
  state: DeliveryReadinessState;
  counts: DeliveryReadinessCounts;
  isBlocked: boolean;
  hasLoadableBoxes: boolean;
  badgeText: string | null;
  badgeClass: string;
  cardMessage: string | null;
  bannerMessage: string | null;
  bannerDetail: string | null;
}

// ===========================================
// Status groupings
// ===========================================

const NOT_STARTED_STATUSES: BoxStatusCode[] = ['WCAC'];
const IN_TRANSIT_STATUSES: BoxStatusCode[] = ['ITKK'];
const WAREHOUSE_STATUSES: BoxStatusCode[] = ['WKKU', 'WKKS', 'WKKDO', 'WKKP'];
const READY_TO_LOAD_STATUSES: BoxStatusCode[] = ['WKKHO'];
const OUT_FOR_DELIVERY_STATUSES: BoxStatusCode[] = ['ITTC'];
const DELIVERED_STATUSES: BoxStatusCode[] = ['WC'];
const FAILED_STATUSES: BoxStatusCode[] = ['WCFC', 'ITTKKFR'];

// ===========================================
// Main function
// ===========================================

/**
 * Computes the delivery readiness state from all boxes on a delivery order.
 * Pass ALL order boxes (before any workflow filtering) to get accurate readiness.
 */
export function getDeliveryReadiness(allBoxes: EnhancedOrderBox[]): DeliveryReadiness {
  const counts: DeliveryReadinessCounts = {
    awaitingCollection: 0,
    inTransit: 0,
    warehouseProcessing: 0,
    readyToLoad: 0,
    outForDelivery: 0,
    delivered: 0,
    failed: 0,
    total: allBoxes.length,
  };

  for (const box of allBoxes) {
    const status = box.statusCode;
    if (NOT_STARTED_STATUSES.includes(status)) counts.awaitingCollection++;
    else if (IN_TRANSIT_STATUSES.includes(status)) counts.inTransit++;
    else if (WAREHOUSE_STATUSES.includes(status)) counts.warehouseProcessing++;
    else if (READY_TO_LOAD_STATUSES.includes(status)) counts.readyToLoad++;
    else if (OUT_FOR_DELIVERY_STATUSES.includes(status)) counts.outForDelivery++;
    else if (DELIVERED_STATUSES.includes(status)) counts.delivered++;
    else if (FAILED_STATUSES.includes(status)) counts.failed++;
  }

  // State determination: highest-stage box present determines overall state
  const state = determineState(counts);
  const hasLoadableBoxes = counts.readyToLoad > 0;
  // Blocked = no boxes currently in ITTC (nothing to scan/deliver) AND not all delivered
  const isBlocked = counts.outForDelivery === 0 && state !== 'completed' && state !== 'in_delivery';

  const { badgeText, badgeClass } = getBadge(state);
  const cardMessage = getCardMessage(state, counts);
  const { bannerMessage, bannerDetail } = getBanner(state, counts);

  return {
    state,
    counts,
    isBlocked,
    hasLoadableBoxes,
    badgeText,
    badgeClass,
    cardMessage,
    bannerMessage,
    bannerDetail,
  };
}

// ===========================================
// Helpers
// ===========================================

function determineState(counts: DeliveryReadinessCounts): DeliveryReadinessState {
  // If all delivered (+ any failed), completed
  if (counts.delivered + counts.failed === counts.total && counts.total > 0) return 'completed';
  // Check from most advanced stage downward
  if (counts.outForDelivery > 0) return 'in_delivery';
  if (counts.readyToLoad > 0) return 'awaiting_load';
  if (counts.warehouseProcessing > 0) return 'warehouse_processing';
  if (counts.inTransit > 0) return 'in_transit';
  return 'not_started';
}

function getBadge(state: DeliveryReadinessState): { badgeText: string | null; badgeClass: string } {
  switch (state) {
    case 'not_started':
    case 'in_transit':
      return { badgeText: 'NOT READY', badgeClass: 'not-ready' };
    case 'warehouse_processing':
      return { badgeText: 'IN WAREHOUSE', badgeClass: 'in-warehouse' };
    case 'awaiting_load':
      return { badgeText: 'AWAITING LOAD', badgeClass: 'awaiting-load' };
    case 'in_delivery':
    case 'completed':
      return { badgeText: null, badgeClass: '' };
  }
}

function getCardMessage(state: DeliveryReadinessState, counts: DeliveryReadinessCounts): string | null {
  switch (state) {
    case 'not_started':
      return 'Boxes awaiting collection';
    case 'in_transit':
      return 'Boxes in transit to warehouse';
    case 'warehouse_processing':
      return 'Boxes in warehouse processing';
    case 'awaiting_load':
      // Only show card message if there are also upstream boxes
      if (counts.awaitingCollection + counts.inTransit + counts.warehouseProcessing > 0) {
        const upstream = counts.awaitingCollection + counts.inTransit + counts.warehouseProcessing;
        return `${upstream} more box${upstream !== 1 ? 'es' : ''} not ready`;
      }
      return null;
    default:
      return null;
  }
}

function getBanner(state: DeliveryReadinessState, counts: DeliveryReadinessCounts): { bannerMessage: string | null; bannerDetail: string | null } {
  switch (state) {
    case 'not_started':
      return {
        bannerMessage: 'All boxes are still with the customer. This order cannot proceed until collection and warehouse processing are complete.',
        bannerDetail: buildBreakdown(counts),
      };
    case 'in_transit':
      return {
        bannerMessage: 'Boxes are being transported to the warehouse. They need to be received and processed before delivery.',
        bannerDetail: buildBreakdown(counts),
      };
    case 'warehouse_processing':
      return {
        bannerMessage: 'Boxes are being processed at the warehouse. They will be ready for loading once preparation is complete.',
        bannerDetail: buildBreakdown(counts),
      };
    case 'awaiting_load':
      // Don't show a blocking banner if there are loadable boxes — existing WKKHO notice handles this
      if (counts.awaitingCollection + counts.inTransit + counts.warehouseProcessing > 0) {
        const upstream = counts.awaitingCollection + counts.inTransit + counts.warehouseProcessing;
        return {
          bannerMessage: null,
          bannerDetail: `${upstream} additional box${upstream !== 1 ? 'es are' : ' is'} still in earlier stages and not yet ready.`,
        };
      }
      return { bannerMessage: null, bannerDetail: null };
    default:
      return { bannerMessage: null, bannerDetail: null };
  }
}

function buildBreakdown(counts: DeliveryReadinessCounts): string {
  const parts: string[] = [];
  if (counts.awaitingCollection > 0) parts.push(`${counts.awaitingCollection} awaiting collection`);
  if (counts.inTransit > 0) parts.push(`${counts.inTransit} in transit`);
  if (counts.warehouseProcessing > 0) parts.push(`${counts.warehouseProcessing} in warehouse`);
  if (counts.readyToLoad > 0) parts.push(`${counts.readyToLoad} ready to load`);
  if (counts.outForDelivery > 0) parts.push(`${counts.outForDelivery} out for delivery`);
  if (counts.delivered > 0) parts.push(`${counts.delivered} delivered`);
  if (counts.failed > 0) parts.push(`${counts.failed} failed`);
  return parts.join(', ');
}
